import React, { useState, useEffect, useRef } from "react";
import { Navigate, Outlet, Link, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  CalendarDays,
  LayoutDashboard,
  Medal,
  Shield,
  Tags,
  Settings,
  Trophy,
  Users,
  Moon,
  Sun,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
} from "lucide-react";
import { useAuth } from "contexts/AuthContext";
import { cn } from "lib/utils";
import brand from "lib/brand";
import { useI18n, LANGUAGE_OPTIONS } from "i18n";

// Menu por capability (menor privilegio): o item so aparece se o usuario
// tiver a capability — e a API valida de novo em cada endpoint.
const menuStructure = [
  {
    id: "dashboard",
    path: "/app/dashboard",
    icon: LayoutDashboard,
    labelKey: "nav.dashboard",
    children: null,
  },
  {
    id: "championships",
    path: "/app/championships",
    icon: Trophy,
    labelKey: "nav.championships",
    capability: ["manage_events"],
    children: null,
  },
  {
    id: "events",
    path: "/app/events",
    icon: CalendarDays,
    labelKey: "nav.events",
    capability: ["manage_events"],
    children: null,
  },
  {
    id: "sports",
    path: "/app/sports",
    icon: Medal,
    labelKey: "nav.sports",
    capability: ["manage_events"],
    children: null,
  },
  {
    id: "sportCategories",
    path: "/app/sport-categories",
    icon: Tags,
    labelKey: "nav.sportCategories",
    capability: ["manage_events"],
    children: null,
  },
  {
    id: "admin",
    icon: Shield,
    labelKey: "nav.admin",
    capability: ["manage_users", "manage_settings"],
    children: [
      {
        id: "accounts",
        path: "/app/admin/accounts",
        labelKey: "nav.accounts",
        icon: Users,
        capability: ["manage_users"],
      },
      {
        id: "settings",
        path: "/app/admin/settings",
        labelKey: "nav.settings",
        icon: Settings,
        capability: ["manage_system"],
      },
    ],
  },
];

function hasCapability(user, capability) {
  if (!capability) return true;
  const caps = user?.capabilities || {};
  return capability.some((c) => !!caps[c]);
}

/** A rota e o item do menu ou uma tela filha dele (`/app/events/<id>/edit`)? */
const isUnder = (pathname, path) =>
  pathname === path || pathname.startsWith(`${path}/`);

const findActiveMenu = (pathname) => {
  for (const menu of menuStructure) {
    // Prefixo, e nao igualdade: o detalhe e a edicao de um registro
    // (`/app/championships/<id>`) pertencem ao mesmo item de menu da listagem.
    // Comparando por igualdade eles caiam no fallback e o marcador ia parar no
    // Dashboard.
    if (menu.path && isUnder(pathname, menu.path)) {
      return { menuId: menu.id, subMenuId: null };
    }
    if (menu.children) {
      const sortedChildren = [...menu.children].sort(
        (a, b) => b.path.length - a.path.length
      );
      for (const child of sortedChildren) {
        if (isUnder(pathname, child.path)) {
          return { menuId: menu.id, subMenuId: child.id };
        }
      }
    }
  }
  // Rota fora do menu: nenhum item marcado — melhor que apontar o Dashboard,
  // que nao e onde o usuario esta.
  return { menuId: null, subMenuId: null };
};

export default function AppLayout({ darkMode, setDarkMode }) {
  const { user, loading, logout, changeLanguage } = useAuth();
  const { t, lang } = useI18n();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });
  const [expandedMenus, setExpandedMenus] = useState({});
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [flyoutPosition, setFlyoutPosition] = useState({ top: 0 });
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const location = useLocation();

  const { menuId: activeMenuId, subMenuId: activeSubMenuId } =
    findActiveMenu(location.pathname);

  useEffect(() => {
    if (!sidebarCollapsed && activeMenuId) {
      setExpandedMenus({ [activeMenuId]: true });
    }
  }, [activeMenuId, sidebarCollapsed]);

  // O tema escuro e exclusivo da area administrativa — a area publica remove a
  // classe ao montar (ver PublicLayout).
  useEffect(() => {
    document.documentElement.classList.toggle("dark", !!darkMode);
  }, [darkMode]);

  useEffect(() => {
    const checkScreenSize = () => setIsSmallScreen(window.innerWidth < 1024);
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", sidebarCollapsed);
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!sidebarCollapsed) setHoveredMenu(null);
  }, [sidebarCollapsed]);

  const effectiveCollapsed = isSmallScreen || sidebarCollapsed;

  const toggleMenu = (menuId) => {
    if (!effectiveCollapsed) {
      setExpandedMenus((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
    }
  };

  const handleMenuHover = (menuId, event) => {
    if (effectiveCollapsed) {
      clearTimeout(hoverTimeoutRef.current);
      setHoveredMenu(menuId);
      if (event && event.currentTarget) {
        const rect = event.currentTarget.getBoundingClientRect();
        setFlyoutPosition({ top: rect.top });
      }
    }
  };

  const handleMenuLeave = () => {
    if (effectiveCollapsed) {
      hoverTimeoutRef.current = setTimeout(() => setHoveredMenu(null), 150);
    }
  };

  const renderMenuItem = (item) => {
    if (!hasCapability(user, item.capability)) return null;

    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus[item.id];
    const isActive = item.id === activeMenuId;
    const isHovered = hoveredMenu === item.id;

    if (!hasChildren) {
      return (
        <div key={item.id}>
          <Link
            to={item.path}
            className={cn(
              "flex items-center transition-all text-sm relative",
              effectiveCollapsed
                ? "justify-center p-2.5"
                : "justify-start gap-3 px-4 py-2.5",
              isActive
                ? "text-emerald-400 font-medium"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
            )}
          >
            <Icon size={18} />
            {!effectiveCollapsed && (
              <span className="font-medium text-sm">{t(item.labelKey)}</span>
            )}
          </Link>
        </div>
      );
    }

    return (
      <div
        key={item.id}
        className="relative"
        onMouseEnter={(e) => handleMenuHover(item.id, e)}
        onMouseLeave={handleMenuLeave}
      >
        <button
          onClick={() => toggleMenu(item.id)}
          className={cn(
            "w-full flex items-center transition-all text-sm",
            effectiveCollapsed
              ? "justify-center p-2.5"
              : "justify-between px-4 py-2.5",
            isActive
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
          )}
        >
          <div className="flex items-center gap-3">
            <Icon size={18} />
            {!effectiveCollapsed && (
              <span className="font-medium text-sm">{t(item.labelKey)}</span>
            )}
          </div>
          {!effectiveCollapsed && (
            <ChevronDown
              size={16}
              className={cn(
                "transition-transform duration-200",
                isExpanded ? "rotate-180" : ""
              )}
            />
          )}
        </button>

        {effectiveCollapsed &&
          isHovered &&
          createPortal(
            <div
              className="fixed min-w-[180px]"
              style={{
                left: "72px",
                top: `${flyoutPosition.top}px`,
                zIndex: 99999,
              }}
              onMouseEnter={(e) => handleMenuHover(item.id, e)}
              onMouseLeave={handleMenuLeave}
            >
              <div className="absolute left-0 top-3 -ml-1.5 w-3 h-3 bg-popover border-l border-t border-border rotate-[-45deg]" style={{ zIndex: 99999 }} />
              <div className="bg-popover border border-border rounded-lg shadow-2xl py-2 ml-1">
                <div className="px-3 py-2 border-b border-border">
                  <span className="font-semibold text-sm">{t(item.labelKey)}</span>
                </div>
                {item.children.filter((child) => hasCapability(user, child.capability)).map((child) => {
                  const isChildActive = child.id === activeSubMenuId;
                  return (
                    <Link
                      key={child.id}
                      to={child.path}
                      className={cn(
                        "block px-4 py-2 text-sm transition-colors",
                        isChildActive
                          ? "text-emerald-400 bg-emerald-400/10 font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                      onClick={() => setHoveredMenu(null)}
                    >
                      {t(child.labelKey)}
                    </Link>
                  );
                })}
              </div>
            </div>,
            document.body
          )}

        {!effectiveCollapsed && isExpanded && (
          <div className="space-y-0.5 bg-black/20">
            {item.children.filter((child) => hasCapability(user, child.capability)).map((child) => {
              const isChildActive = child.id === activeSubMenuId;
              return (
                <Link
                  key={child.id}
                  to={child.path}
                  className={cn(
                    "block w-full pl-11 pr-4 py-2 text-sm transition-colors",
                    isChildActive
                      ? "text-emerald-400 font-medium"
                      : "text-muted-foreground/70 hover:text-foreground hover:bg-white/5"
                  )}
                >
                  {t(child.labelKey)}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/app/login" replace />;
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-background">
      {/* Top Header */}
      <header className="flex items-center justify-between bg-card border-b border-border z-50">
        <div className="flex items-center justify-center px-4 py-3 border-r border-border w-52">
          <img
            src={darkMode ? brand.logoDark : brand.logo}
            alt={brand.name}
            className="h-7 w-auto drop-shadow-[0_0_10px_rgba(255,255,255,0.4)]"
          />
        </div>

        <div className="flex items-center gap-4 flex-1 justify-end px-6 py-3">
          {/* Volta ao site publico (a area padrao, em `/`) sem sair da conta. */}
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <ArrowLeft size={16} />
            <span>{t("nav.backToSite")}</span>
          </Link>

          <div className="h-6 w-px bg-border" />

          {/* User info */}
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">
              {user?.first_name || user?.email}
            </span>
          </div>

          <div className="h-6 w-px bg-border" />

          {/* Idioma da CONTA: a escolha e gravada no perfil (PATCH
              /api/auth/me/), senao o proximo carregamento a sobrescreveria
              com o idioma efetivo devolvido pelo /api/auth/me/. */}
          <select
            value={lang}
            onChange={(e) => changeLanguage(e.target.value)}
            aria-label={t("common.language")}
            className="bg-transparent text-sm text-muted-foreground hover:text-foreground focus:outline-none cursor-pointer"
          >
            {LANGUAGE_OPTIONS.map((l) => (
              <option key={l.value} value={l.value} className="bg-card text-foreground">
                {l.label}
              </option>
            ))}
          </select>

          <div className="h-6 w-px bg-border" />

          {/* Dark Mode Toggle */}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          <div className="h-6 w-px bg-border" />

          {/* Logout */}
          <button
            onClick={logout}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <LogOut size={16} />
            <span>{t("nav.logout")}</span>
          </button>
        </div>
      </header>

      {/* Main container - Sidebar + Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside
          className={cn(
            "relative bg-card border-r border-border transition-all duration-300 ease-in-out flex-shrink-0",
            effectiveCollapsed ? "w-16" : "w-52"
          )}
        >
          <div className="flex flex-col h-full">
            <nav
              className={cn(
                "flex-1 space-y-0.5 overflow-y-auto scrollbar-thin pt-2",
                effectiveCollapsed ? "px-2" : "px-0"
              )}
            >
              {menuStructure.map(renderMenuItem)}
            </nav>

            {/* Footer with collapse toggle */}
            <div
              className={cn(
                "border-t border-border transition-all duration-300 flex items-end relative",
                isSmallScreen ? "hidden" : "flex",
                effectiveCollapsed
                  ? "p-2 justify-center min-h-[60px]"
                  : "p-3 justify-between min-h-[70px]"
              )}
            >
              {!effectiveCollapsed && (
                <div className="text-xs text-muted-foreground">
                  <div className="text-[11px] font-medium">
                    {brand.name} v{brand.version}
                  </div>
                  <div className="text-[10px] text-muted-foreground/60">
                    &copy; {brand.name} {new Date().getFullYear()}
                  </div>
                </div>
              )}

              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className={cn(
                  "flex items-center justify-center transition-all duration-200",
                  effectiveCollapsed
                    ? "w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 hover:border-primary/50 shadow-sm"
                    : "absolute right-0 bottom-[10px] w-[35px] h-[45px] rounded-l-lg bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground"
                )}
              >
                {effectiveCollapsed ? (
                  <ChevronRight size={18} />
                ) : (
                  <ChevronLeft size={20} />
                )}
              </button>
            </div>
          </div>
        </aside>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 bg-background relative">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
